import {
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ScoreBadge } from '../components/ScoreBadge.js';
import { buildMapsUrl, listObjects, markContacted, profileLabel } from '../lib/api.js';

function fmtPrice(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}
function fmtArea(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${Number.isInteger(n) ? n : n.toFixed(1)} m²`;
}

/**
 * 🎯 Matches — Immobilien, die BEIDE positiv geswiped haben. Nächster Schritt: Makler/
 * Verkäufer kontaktieren. „Kontakt aufgenommen" schiebt das Objekt nach „In Kontakt".
 */
export function Matches(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['matches'],
    queryFn: () => listObjects({ status: 'match', minScore: 0, limit: 200 }),
    refetchInterval: 60_000,
  });

  const contactMut = useMutation({
    mutationFn: (id: number) => markContacted(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['matches'] });
      void qc.invalidateQueries({ queryKey: ['contacted'] });
      void qc.invalidateQueries({ queryKey: ['objects'] });
      void qc.invalidateQueries({ queryKey: ['nav-badge-match'] });
    },
    onError: (err) => notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const objs = data?.objects ?? [];

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="xs">
        <Stack gap={4}>
          <Title order={1}>🎯 Matches</Title>
          <Text c="dimmed" size="sm">
            {objs.length} Objekt{objs.length === 1 ? '' : 'e'}, {objs.length === 1 ? 'das' : 'die'} ihr
            beide wollt — jetzt Kontakt aufnehmen.
          </Text>
        </Stack>
        <Button component={Link} to="/contacted" variant="subtle" size="xs">
          ✓ In Kontakt →
        </Button>
      </Group>

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}
      {error && (
        <Card>
          <Text c="red.7">Fehler: {String(error)}</Text>
        </Card>
      )}

      {data && objs.length === 0 && (
        <Card padding="xl">
          <Center py="lg">
            <Stack align="center" gap="md">
              <Text style={{ fontSize: '2.5rem' }}>🎯</Text>
              <Text size="lg" fw={600} ta="center">
                Noch keine Matches
              </Text>
              <Text c="dimmed" size="sm" ta="center" maw={320}>
                Sobald ihr beide dieselbe Immobilie mit „Hot" swiped, erscheint sie hier.
              </Text>
              <Button component={Link} to="/" variant="light" mt="sm">
                Zum Swipe →
              </Button>
            </Stack>
          </Center>
        </Card>
      )}

      <Stack gap="md">
        {objs.map((o) => (
          <Card
            key={o.id}
            padding="lg"
            style={{ borderLeft: '4px solid #f59e0b', background: 'rgba(251,191,36,0.05)' }}
          >
            <Group align="flex-start" wrap="nowrap" gap="md">
              <ScoreBadge score={o.score?.effectiveTotal} size="md" killed={false} />
              <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                <Group gap={6} wrap="wrap">
                  <Badge
                    size="sm"
                    color={o.assignedProfile === 'B_buero' ? 'cyan' : 'violet'}
                    variant="light"
                    tt="none"
                  >
                    {profileLabel(o.assignedProfile)}
                  </Badge>
                  <Badge size="sm" color="yellow" variant="filled" tt="none">
                    🎯 Match
                  </Badge>
                </Group>
                <Text
                  fw={700}
                  size="md"
                  lineClamp={2}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/objects/${o.id}`)}
                >
                  {o.listing?.title ?? '—'}
                </Text>
                <Text size="sm" c="dimmed">
                  {o.address ?? '—'}
                </Text>
                <Group gap="md" mt={4} wrap="wrap">
                  <Fact label="Fläche" value={fmtArea(o.latestSnapshot?.areaSqm)} />
                  <Fact label="Preis" value={fmtPrice(o.latestSnapshot?.price)} />
                  <Fact
                    label="Pendel"
                    value={o.bestCommuteMin !== null ? `${Math.round(o.bestCommuteMin)} min` : '—'}
                  />
                </Group>
                <Group gap="sm" mt={4} wrap="wrap">
                  {o.listing?.url && (
                    <Button
                      component="a"
                      href={o.listing.url}
                      target="_blank"
                      rel="noreferrer"
                      size="xs"
                      variant="light"
                      color="indigo"
                    >
                      🌐 Inserat
                    </Button>
                  )}
                  <Button
                    component="a"
                    href={buildMapsUrl(o)}
                    target="_blank"
                    rel="noreferrer"
                    size="xs"
                    variant="light"
                    color="indigo"
                  >
                    🗺 Maps
                  </Button>
                  <Button component={Link} to={`/objects/${o.id}`} size="xs" variant="subtle">
                    📊 Details
                  </Button>
                  <Button
                    size="xs"
                    color="teal"
                    onClick={() => contactMut.mutate(o.id)}
                    loading={contactMut.isPending && contactMut.variables === o.id}
                  >
                    ☎ Kontakt aufgenommen
                  </Button>
                </Group>
              </Stack>
            </Group>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

function Fact({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text
        size="xs"
        c="dimmed"
        tt="uppercase"
        fw={500}
        style={{ letterSpacing: '0.04em', fontSize: '0.65rem' }}
      >
        {label}
      </Text>
      <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Stack>
  );
}
